import { ArrowBendUpRightIcon, FolderMinusIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router'
import { Routes } from '../../routes.ts'
import type { Repository } from '../../utils/api-client/api-schemas.ts'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './project-row-card.css.ts'

export type ProjectRepositoryCardProps = {
  repository: Pick<Repository, 'id' | 'full_name' | 'type'>
  /** The row's own detail — a risk score, when the repository has one. */
  riskScore?: number
  /** When the repository was last scanned, already phrased ("2 years ago"). */
  lastScanned?: string
  /** Offered to administrators only; a reader gets the row without its actions. */
  onMove?: () => unknown
  onRemove?: () => unknown
}

/** One repository in a project: a row card that opens the repository, with move and remove on hover. */
export const ProjectRepositoryCard: React.FC<ProjectRepositoryCardProps> = ({
  repository,
  riskScore,
  lastScanned,
  onMove,
  onRemove,
}) => {
  const navigate = useNavigate()
  const open = () => void navigate(Routes.RepositoryDetailsPage.createPath({ repositoryId: repository.id }))

  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      aria-label={`Open ${repository.full_name}`}
      onClick={open}
      onKeyDown={event => {
        // A key pressed on one of the action buttons is the button's, not the card's.
        if (event.target !== event.currentTarget) {
          return
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          open()
        }
      }}
    >
      <span className={styles.badge}>
        <SCMIcon type={repository.type} size="medium" />
      </span>
      <span className={styles.meta}>
        <p className={styles.name}>{repository.full_name}</p>
        <p className={styles.sub}>
          <span>Risk {riskScore ?? '—'}</span>
          <i className={styles.subSeparator}>·</i>
          <span>Last scan {lastScanned ?? 'never'}</span>
        </p>
      </span>
      {(onMove || onRemove) && (
        <span className={styles.actions}>
          {onMove && (
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label={`Move ${repository.full_name} to another project`}
                  onClick={event => {
                    event.stopPropagation()
                    onMove()
                  }}
                >
                  <ArrowBendUpRightIcon size={18} weight="light" aria-hidden />
                </button>
              }
            >
              Move to another project
            </TooltipWithHover>
          )}
          {onRemove && (
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label={`Remove ${repository.full_name} from this project`}
                  onClick={event => {
                    event.stopPropagation()
                    onRemove()
                  }}
                >
                  <FolderMinusIcon size={18} weight="light" aria-hidden />
                </button>
              }
            >
              Remove from this project
            </TooltipWithHover>
          )}
        </span>
      )}
    </div>
  )
}
